import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const LEVELS = ["standard", "preferred", "premier", "strategic"];
const EMPTY = { code: "", label: "", sku_prefix: "", product_type: "", family: "", mounting: "Loose", format_mm: "", specs: "",
  pieces_per_box: "", sqft_per_box: "", boxes_per_pallet: "", supplier_cost: "", inbound_freight_alloc: "",
  standard: "", preferred: "", premier: "", strategic: "", container_price: "", container_floor: "" };
const NUM = ["pieces_per_box", "sqft_per_box", "boxes_per_pallet", "supplier_cost", "inbound_freight_alloc", "container_price", "container_floor"];

const FIELDS = [
  ["code", "Format code (e.g. 1x4)"], ["label", "Format name"], ["sku_prefix", "SKU prefix"], ["product_type", "Product type"],
  ["family", "Family name"], ["format_mm", "Dimensions (mm)"], ["pieces_per_box", "Pieces / box"], ["sqft_per_box", "Sq ft / box"],
  ["boxes_per_pallet", "Boxes / pallet"], ["supplier_cost", "Supplier cost $/box"], ["inbound_freight_alloc", "Inbound alloc $/box"],
  ["standard", "Standard $/box"], ["preferred", "Preferred $/box"], ["premier", "Premier $/box"], ["strategic", "Strategic $/box"],
  ["container_price", "Container $/box (optional)"], ["container_floor", "Container floor (optional)"],
];

function payload(f, dry) {
  const num = (v) => (v === "" ? null : Number(v));
  return {
    code: f.code.trim(), label: f.label.trim(), sku_prefix: f.sku_prefix.trim().toUpperCase(), product_type: f.product_type.trim(),
    family: f.family.trim() || null, mounting: f.mounting, format_mm: f.format_mm.trim(), specs: f.specs.trim(),
    ...Object.fromEntries(NUM.map((k) => [k, num(f[k])])),
    prices: Object.fromEntries(LEVELS.map((l) => [l, num(f[l])])), dry_run: dry, note: "Admin Pricing — Add Product Format",
  };
}

export function AddFormatDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setF((x) => ({ ...x, [k]: e.target.value })); setCheck(null); };
  const landed = (Number(f.supplier_cost) || 0) + (Number(f.inbound_freight_alloc) || 0);
  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pricing/formats", payload(f, dry));
      if (dry) { setCheck(data.link); return; }
      toast.success(`${data.code} added · ${data.linked_variants} catalog variant(s) linked`);
      setF(EMPTY); setCheck(null); onOpenChange(false); onCreated?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail?.message ? `${e.response.data.detail.message}: ${e.response.data.detail.issues.map((i) => i.sku).join(", ")}` : e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-white" data-testid="add-format-dialog">
        <DialogHeader><DialogTitle>Add product format</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Uses the same pricing engine as 4×4, 2×6 and 2×2. Existing catalog variants with this SKU prefix are linked — none are created. Pricing does not make a product sellable; variants keep their catalog status.</p>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-warmgrey">{l}</Label>
              <Input value={f[k]} onChange={set(k)} type={NUM.includes(k) || LEVELS.includes(k) ? "number" : "text"} step="0.01" min="0" data-testid={`add-format-${k}`} className="h-10 bg-white" />
            </div>
          ))}
          <div className="space-y-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-warmgrey">Presentation</Label>
            <select value={f.mounting} onChange={set("mounting")} data-testid="add-format-mounting" className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
              <option>Loose</option><option>Mesh-mounted</option>
            </select>
          </div>
          <div className="col-span-2 space-y-1 sm:col-span-3">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-warmgrey">Specifications</Label>
            <Input value={f.specs} onChange={set("specs")} data-testid="add-format-specs" className="h-10 bg-white" />
          </div>
        </div>
        <p className="text-sm text-charcoal" data-testid="add-format-landed">Calculated landed cost: ${landed.toFixed(2)}/box</p>
        {check && (
          <div className="rounded-md border border-border bg-sand/30 p-3 text-xs" data-testid="add-format-link-result">
            <p>{check.variants} existing catalog variant(s) with prefix {f.sku_prefix.toUpperCase()} · {check.sellable} sellable</p>
            {check.issues.length ? <p className="mt-1 text-status-reserved" data-testid="add-format-link-issues">{check.issues.length} mismatch(es): {check.issues.slice(0, 5).map((i) => `${i.sku} (${i.problems.join("; ")})`).join(" · ")}</p>
              : <p className="mt-1 text-status-available">All match this format — ready to link.</p>}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={() => run(true)} data-testid="add-format-check">Check catalog link</Button>
          <Button disabled={busy || !check || check.issues.length > 0} onClick={() => run(false)} className="bg-terracotta text-white hover:bg-charcoal" data-testid="add-format-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save format"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
